import { useEffect, useState } from "react";
import { useFlags } from "launchdarkly-react-client-sdk";
import { ActionType, type FCWithChildren } from "../@types";
import {
  getTealiumAsyncScriptUrl,
  getTealiumEnvironment,
  getTealiumSyncScriptUrl,
} from "../helpers/tealiumHandlerHelpers";
import { getEnvironment } from "../helpers/urlHelpers";
import { devDebug } from "../helpers/loggingHelpers";
import { useAppContext } from "../helpers/context-hooks/useAppContext";

/**
 * NOTE: `AppState.tealiumLoaded` defaults to `false` on every page load
 */
export const TealiumHandler: FCWithChildren = ({ children }) => {
  const {
    state: { tealiumLoaded },
    dispatch,
  } = useAppContext();
  const { feEnableTealium, tmpFeEnableFirstPartyTealiumSource } =
    useFlags() as {
      feEnableTealium: boolean;
      tmpFeEnableFirstPartyTealiumSource: boolean;
    };
  const [script1Inserted, setScript1Inserted] = useState(false);
  const [script2Inserted, setScript2Inserted] = useState(false);
  const [script3Inserted, setScript3Inserted] = useState(false);
  const [script3Loaded, setScript3Loaded] = useState(false);

  const env = getEnvironment();
  const tealiumEnv = getTealiumEnvironment(env);

  /***
   * 1. Insert Tealium script tag
   */
  useEffect(() => {
    if (feEnableTealium && !script1Inserted) {
      const script1 = document.createElement("script");
      script1.innerHTML =
        "var utag_data; window.utag_cfg_ovrd = { noview: true };";
      script1.type = "text/javascript";
      document.head.appendChild(script1);
      setScript1Inserted(true);
    }
  }, [feEnableTealium, script1Inserted]);

  /***
   * 2. Insert Tealium script tag #2 after the first is inserted
   */
  useEffect(() => {
    if (script1Inserted && !script2Inserted) {
      const script2 = document.createElement("script");
      script2.type = "text/javascript";
      script2.src = getTealiumSyncScriptUrl(
        tealiumEnv,
        tmpFeEnableFirstPartyTealiumSource
      );
      script2.defer = true;
      document.head.appendChild(script2);
      setScript2Inserted(true);
    }
  }, [
    script1Inserted,
    script2Inserted,
    tealiumEnv,
    tmpFeEnableFirstPartyTealiumSource,
  ]);

  /***
   * 3. Insert Tealium script tag #3 after the second is inserted
   */
  useEffect(() => {
    if (script2Inserted && !script3Inserted) {
      const script3 = document.createElement("script");
      script3.src = getTealiumAsyncScriptUrl(
        tealiumEnv,
        tmpFeEnableFirstPartyTealiumSource
      );
      script3.type = "text/javascript";
      script3.async = true;
      script3.onload = (): void => {
        setScript3Loaded(true);
      };
      document.body.appendChild(script3);
      setScript3Inserted(true);
    }
  }, [
    dispatch,
    script2Inserted,
    script3Inserted,
    tealiumEnv,
    tmpFeEnableFirstPartyTealiumSource,
  ]);

  /***
   * 4. Wait for the load event of Tealium script #3 to dispatch `tealiumLoaded`
   */
  useEffect(() => {
    if (script3Inserted && script3Loaded && !tealiumLoaded) {
      dispatch({
        type: ActionType.SET_TEALIUM_LOADED,
        payload: true,
      });
    }
  }, [dispatch, script3Inserted, script3Loaded, tealiumLoaded]);

  /***
   * 5. Log for dev purposes
   */
  useEffect(() => {
    if (tealiumLoaded) {
      devDebug("☕️ Tealium loaded!");
    }
  }, [tealiumLoaded]);

  /***
   * If the flag is enabled, Tealium will be enabled
   */
  return <>{children}</>;
};
